import { forwardRef, type ReactNode, type ButtonHTMLAttributes } from 'react';

import { ListItem } from '../list-item';

export interface MenuItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Item content */
  children: ReactNode;
  /** Content to display on the right side */
  rightContent?: ReactNode;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Whether this is a destructive action (displays in red) */
  destructive?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Whether this item is currently active/highlighted */
  isActive?: boolean;
  /** Additional className for outer container */
  className?: string;
  /** Additional className for inner container */
  innerClassName?: string;
}

/**
 * MenuItem - menu item component for action menus
 *
 * Built on ListItem primitive with menu-specific defaults.
 * Can be used independently or within Menu/Dropdown components.
 *
 * @example
 * ```tsx
 * // Standalone usage
 * <MenuItem onClick={() => console.log('clicked')}>Edit</MenuItem>
 *
 * // Disabled
 * <MenuItem disabled>Archive</MenuItem>
 *
 * // Destructive
 * <MenuItem destructive onClick={handleDelete}>Delete</MenuItem>
 *
 * // With right content
 * <MenuItem rightContent={<span className="text-gray-400">⌘K</span>}>
 *   Search
 * </MenuItem>
 * ```
 */
export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  (
    {
      children,
      rightContent,
      disabled = false,
      destructive = false,
      size = 'md',
      isActive = false,
      className = '',
      innerClassName = '',
      ...props
    },
    ref
  ) => {
    return (
      <ListItem
        ref={ref}
        role="menuitem"
        disabled={disabled}
        size={size}
        isActive={isActive}
        variant={destructive ? 'destructive' : 'default'}
        rightContent={rightContent}
        className={className}
        innerClassName={innerClassName}
        {...props}
      >
        {children}
      </ListItem>
    );
  }
);

MenuItem.displayName = 'MenuItem';
